// Fix W (2026-09-02): asistente para imputar las compras anteriores a la 2.0.
// Lista las líneas de los pedidos de materiales de la obra que no tienen ítem
// del presupuesto ni categoría, con la sugerencia del backend
// (GET /obras/{id}/imputar-compras/propuestas) y un desplegable para decidir a
// qué material del listado desglosado, a qué ítem del presupuesto o a qué
// categoría (menores / imprevisto / equipamiento) va cada una. Misma regla que
// AsignarTareasModal: NUNCA se preselecciona nada al cargar; el usuario decide
// fila por fila o usa los dos botones explícitos (aceptar altas / marcar las
// sin coincidencia como Materiales Menores). "Aplicar" manda solo las filas
// con decisión (POST /obras/{id}/imputar-compras).
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Check, ShoppingCart, Wand2, Search, ChevronDown } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";
import { toast } from "sonner";

const opts = { withCredentials: true };
const PAGE_SIZE = 50;
const ORDEN_CONF = { alta: 0, media: 1, baja: 2 };
const CATEGORIAS = [
  { value: "cat:menores", label: "Materiales Menores (sin ítem del presupuesto)" },
  { value: "cat:imprevistos", label: "Imprevisto (sin ítem del presupuesto)" },
  { value: "cat:equipamientos", label: "Equipamiento (sin ítem del presupuesto)" },
  { value: "cat:herramientas", label: "Herramienta (sin ítem del presupuesto)" },
];

const badge = (conf) => conf === "alta" ? "bg-emerald-100 text-emerald-700"
  : conf === "media" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500";

const matKey = (descripcion, unidad) => `mat:${descripcion}|${unidad}`;

// Fix AI (2026-09-04): "en este desplegable donde tenemos las opciones de
// imputación, si se puede agregar un filtro para agilizar la busqueda del item
// o material" (Edson, 04/09). El <select> nativo no se puede filtrar y corta las
// descripciones largas en una línea. Este reemplazo abre un panel con buscador y
// muestra cada opción completa. Guarda exactamente el mismo `value` que antes,
// así que el payload que se manda al backend no cambia.
function SelectBuscable({ value, grupos, onChange, testId }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);

  const elegida = grupos.flatMap(g => g.items).find(o => o.value === value) || null;

  const abrir = () => {
    const r = btnRef.current.getBoundingClientRect();
    const alto = Math.min(360, window.innerHeight - 32);
    const cabeAbajo = window.innerHeight - r.bottom > alto + 8;
    const ancho = Math.max(r.width, 440);
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - ancho - 8)),
      top: cabeAbajo ? r.bottom + 4 : Math.max(8, r.top - alto - 4),
      width: ancho, alto,
    });
    setQ("");
    setAbierto(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const term = q.trim().toLowerCase();
  const filtrados = grupos
    .map(g => ({ ...g, items: term ? g.items.filter(o => o.label.toLowerCase().includes(term)) : g.items }))
    .filter(g => g.items.length > 0);
  const nRes = filtrados.reduce((s, g) => s + g.items.length, 0);

  return (
    <>
      <button type="button" ref={btnRef} onClick={abrir}
        className={`w-full text-left rounded-lg border px-2 py-1 text-[11px] bg-white flex items-start justify-between gap-1 ${elegida ? "border-emerald-300" : "border-gray-200"}`}
        data-testid={testId}>
        <span className={`flex-1 whitespace-normal break-words ${elegida ? "text-gray-800" : "text-gray-400"}`}>
          {elegida ? elegida.label : "— Sin decidir —"}
        </span>
        <ChevronDown size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
      </button>
      {abierto && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[80]" onClick={() => setAbierto(false)} onWheel={() => setAbierto(false)} />
          <div className="fixed z-[81] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.alto }}
            data-testid={`${testId}-panel`}>
            <div className="flex items-center gap-1.5 px-2 py-2 border-b border-gray-100">
              <Search size={12} className="text-gray-400 flex-shrink-0" />
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); } }}
                placeholder="Filtrar material o ítem..." className="w-full text-[11px] outline-none"
                data-testid={`${testId}-buscar`} />
              <span className="text-[10px] text-gray-400 flex-shrink-0">{nRes}</span>
            </div>
            <div className="overflow-y-auto">
              <button type="button" onClick={() => { onChange(""); setAbierto(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50">— Sin decidir —</button>
              {filtrados.map(g => (
                <div key={g.label}>
                  <p className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wide bg-gray-50">{g.label}</p>
                  {g.items.map(o => (
                    <button key={o.value} type="button" onClick={() => { onChange(o.value); setAbierto(false); }}
                      className={`w-full text-left px-3 py-1.5 text-[11px] whitespace-normal break-words hover:bg-[#EBF3FB] ${o.value === value ? "bg-emerald-50 font-semibold text-emerald-800" : "text-gray-700"}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              ))}
              {nRes === 0 && <p className="px-3 py-4 text-[11px] text-gray-400 text-center">Sin coincidencias</p>}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export function ImputarComprasModal({ open, obraId, onClose, onApplied }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({}); // `${pm_id}:${idx}` -> valor del desplegable
  const [filtro, setFiltro] = useState("todas");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/obras/${obraId}/imputar-compras/propuestas`, opts);
      setData(data);
      setSelected({});
      setPage(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron cargar las compras anteriores");
    } finally { setLoading(false); }
  }, [obraId]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  // Fix AI (2026-09-04): el botón "atrás" del navegador cierra este modal.
  useCerrarConAtras(open, onClose);

  // Filas planas (una por línea de pedido), ordenadas: alta → media → baja, y
  // dentro de cada confianza por fecha del pedido.
  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.pedidos || []).forEach(p => (p.lineas || []).forEach(l => out.push({
      key: `${p.pm_id}:${l.idx}`, pm_id: p.pm_id, number: p.number, created_at: p.created_at,
      presupuesto_id: p.presupuesto_id, presupuesto_codigo: p.presupuesto_codigo, ...l,
    })));
    out.sort((a, b) => (ORDEN_CONF[a.confianza] - ORDEN_CONF[b.confianza]) || String(a.created_at).localeCompare(String(b.created_at)));
    return out;
  }, [data]);

  // Opciones del desplegable por presupuesto: sugerencias primero, después las
  // categorías, después todos los materiales del listado y todos los ítems.
  const opcionesPorPto = useMemo(() => {
    const m = {};
    Object.entries(data?.candidatos || {}).forEach(([bid, c]) => {
      m[bid] = {
        materiales: (c.materiales || []).map(x => ({ value: matKey(x.descripcion, x.unidad), label: `${x.descripcion} (${x.unidad})${x.origen === "item_directo" ? "" : " · desglose"}`, payload: { accion: "material", descripcion: x.descripcion, unidad: x.unidad } })),
        items: (c.items || []).map(x => ({ value: `item:${x.item_uid}`, label: `${x.numero ? x.numero + " — " : ""}${x.descripcion} (${x.unidad})`, payload: { accion: "item", item_uid: x.item_uid } })),
      };
    });
    return m;
  }, [data]);

  const payloadDe = (row, value) => {
    if (!value) return null;
    if (value.startsWith("cat:")) return { accion: "categoria", categoria: value.slice(4) };
    const o = opcionesPorPto[row.presupuesto_id];
    const found = [...(o?.materiales || []), ...(o?.items || [])].find(x => x.value === value);
    return found ? found.payload : null;
  };

  const valorSugerencia = (s) => s.tipo === "material" ? matKey(s.descripcion, s.unidad) : `item:${s.item_uid}`;

  const filtered = useMemo(() => rows.filter(r => {
    const okF = filtro === "todas" ? true : filtro === "con_decision" ? !!selected[r.key] : r.confianza === filtro;
    const okS = !search || (r.material_name || "").toLowerCase().includes(search.toLowerCase()) || (r.number || "").toLowerCase().includes(search.toLowerCase());
    return okF && okS;
  }), [rows, filtro, search, selected]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const nDecididas = Object.values(selected).filter(Boolean).length;

  const aceptarAltas = () => {
    setSelected(prev => {
      const next = { ...prev };
      rows.forEach(r => { if (r.confianza === "alta" && r.sugerencias?.[0] && !next[r.key]) next[r.key] = valorSugerencia(r.sugerencias[0]); });
      return next;
    });
  };

  const marcarBajasComoMenores = () => {
    setSelected(prev => {
      const next = { ...prev };
      rows.forEach(r => { if (r.confianza === "baja" && !next[r.key]) next[r.key] = "cat:menores"; });
      return next;
    });
  };

  const aplicar = async () => {
    const lineas = [];
    rows.forEach(r => {
      const p = payloadDe(r, selected[r.key]);
      if (p) lineas.push({ pm_id: r.pm_id, idx: r.idx, ...p });
    });
    if (!lineas.length) { toast.error("No hay decisiones para aplicar"); return; }
    setSaving(true);
    try {
      const { data: r } = await axios.post(`${API}/obras/${obraId}/imputar-compras`, { lineas }, opts);
      toast.success(`${r.pm_lineas} línea(s) imputada(s) en ${r.pm} pedido(s) · ${r.oc} orden(es) de compra actualizada(s)${r.omitidas ? ` · ${r.omitidas} omitida(s)` : ""}`);
      onApplied?.(r);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron imputar las compras");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const res = data?.resumen || {};

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="imputar-compras-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[88vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart size={16} className="text-[#EA580C]" /> Imputar compras anteriores
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? "Analizando los pedidos de la obra..." : `${res.lineas || 0} línea(s) sin imputar en ${res.pedidos || 0} pedido(s) · ${res.alta || 0} con coincidencia alta · ${res.media || 0} media · ${res.baja || 0} sin coincidencia clara · ${res.ya_resueltas || 0} ya resueltas`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="imputar-compras-close-btn"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10" data-testid="imputar-compras-vacio">No quedan compras sin imputar en esta obra.</p>
          ) : (
            <>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Cada línea se imputa a un material del listado desglosado del presupuesto, a un ítem del presupuesto, o se marca como material menor / imprevisto / equipamiento. No se cambia ninguna cantidad ni ningún precio: solo se completa a qué corresponde cada compra, para que el consumo por material la cuente.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar por material o número de pedido..."
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs flex-1 min-w-[180px]" data-testid="imputar-compras-search" />
                <select value={filtro} onChange={e => { setFiltro(e.target.value); setPage(0); }}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="imputar-compras-filter">
                  <option value="todas">Todas</option>
                  <option value="alta">Coincidencia alta</option>
                  <option value="media">Coincidencia media</option>
                  <option value="baja">Sin coincidencia clara</option>
                  <option value="con_decision">Con decisión</option>
                </select>
                <button onClick={aceptarAltas} disabled={!res.alta}
                  className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="imputar-compras-aceptar-altas">
                  <Check size={13} /> Aceptar las {res.alta || 0} de coincidencia alta
                </button>
                <button onClick={marcarBajasComoMenores} disabled={!res.baja}
                  className="flex-shrink-0 flex items-center gap-1.5 border border-gray-300 hover:border-[#EA580C] disabled:opacity-50 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="imputar-compras-marcar-menores">
                  <Wand2 size={13} /> Las {res.baja || 0} sin coincidencia → Materiales Menores
                </button>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#EBF3FB]">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Pedido</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Compra</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">Sugerencia</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide w-[34%]">Imputar a</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(r => {
                      const s0 = r.sugerencias?.[0];
                      const o = opcionesPorPto[r.presupuesto_id] || { materiales: [], items: [] };
                      return (
                        <tr key={r.key} className={`border-t border-gray-100 ${selected[r.key] ? "bg-emerald-50/40" : ""}`} data-testid={`imputar-compras-row-${r.pm_id}-${r.idx}`}>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            <p className="font-semibold text-gray-800">{r.number}</p>
                            <p className="text-[10px] text-gray-400">{String(r.created_at || "").slice(0, 10)}{r.presupuesto_codigo ? ` · ${r.presupuesto_codigo}` : ""}</p>
                          </td>
                          <td className="px-3 py-2 text-gray-800 max-w-[260px]">
                            {/* Fix AI (2026-09-04): la descripción completa, en varias
                                líneas — "ese texto oculto puede contener información
                                para poder imputar correctamente" (Edson, 04/09). */}
                            <p className="font-medium whitespace-normal break-words" title={r.material_name}>{r.material_name}</p>
                            <p className="text-[10px] text-gray-400">{r.quantity} {r.unit}</p>
                          </td>
                          <td className="px-3 py-2">
                            {s0 ? (
                              <span className={`inline-flex items-start gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold whitespace-normal break-words ${badge(r.confianza)}`} title={s0.motivo || ""}>
                                {s0.descripcion} ({s0.unidad}) · {Math.round(s0.puntaje * 100)}%{s0.motivo ? ` · ${s0.motivo}` : ""}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-[10px]">sin coincidencia</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <SelectBuscable
                              value={selected[r.key] || ""}
                              onChange={v => setSelected(prev => ({ ...prev, [r.key]: v || null }))}
                              testId={`imputar-compras-select-${r.pm_id}-${r.idx}`}
                              grupos={[
                                ...((r.sugerencias || []).length > 0 ? [{
                                  label: "Sugerencias",
                                  items: r.sugerencias.map(s => ({ value: valorSugerencia(s), label: `${s.descripcion} (${s.unidad}) · ${Math.round(s.puntaje * 100)}%` })),
                                }] : []),
                                { label: "Sin ítem del presupuesto", items: CATEGORIAS },
                                { label: "Materiales del listado del presupuesto", items: o.materiales },
                                { label: "Ítems del presupuesto", items: o.items },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    {pageRows.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Sin resultados para este filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{filtered.length} línea(s) · página {page + 1} de {totalPages}</span>
                  <div className="flex gap-1">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="px-2 py-1 border border-gray-200 rounded-lg disabled:opacity-40" data-testid="imputar-compras-prev">Anterior</button>
                    <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-2 py-1 border border-gray-200 rounded-lg disabled:opacity-40" data-testid="imputar-compras-next">Siguiente</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 sticky bottom-0 bg-white">
          <span className="text-xs text-gray-500">{nDecididas} decisión(es) lista(s) para aplicar</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600" data-testid="imputar-compras-cancel">Cerrar</button>
            <button onClick={aplicar} disabled={saving || nDecididas === 0}
              className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-semibold"
              data-testid="imputar-compras-aplicar">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Aplicar {nDecididas > 0 ? `(${nDecididas})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
